import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { Plus, Trash2 } from "lucide-react";

const TYPES = [
  { k: "lead_sources", label: "Lead Sources" },
  { k: "lead_statuses", label: "Lead Statuses" },
  { k: "pipeline_stages", label: "Pipeline Stages" },
  { k: "service_categories", label: "Service Categories" },
  { k: "interaction_types", label: "Interaction Types" },
  { k: "document_categories", label: "Document Categories" },
  { k: "payment_methods", label: "Payment Methods" },
];

export default function CrmSettings() {
  const [active, setActive] = useState(TYPES[0].k);
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [seeding, setSeeding] = useState(false);

  const load = async () => { const { data } = await api.get(`/crm/config/${active}`); setItems(data.items || []); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);

  // Auto-seed default lookups the first time the page is opened for a workspace.
  // seed_defaults is idempotent server-side, so we call it unconditionally to
  // cover partially-seeded workspaces too.
  useEffect(() => {
    (async () => {
      try {
        setSeeding(true);
        await api.post(`/crm/config/seed_defaults`);
        await load();
      } catch (_) { /* ignore */ }
      finally { setSeeding(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const add = async () => {
    if (!newName.trim()) return;
    try { await api.post(`/crm/config/${active}`, { name: newName, order: items.length }); setNewName(""); load(); }
    catch (e) { toast.error(formatApiError(e, "Failed")); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove?")) return;
    await api.delete(`/crm/config/${active}/${id}`); load();
  };

  const seedNow = async () => {
    setSeeding(true);
    try {
      await api.post(`/crm/config/seed_defaults`);
      toast.success("Defaults seeded");
      load();
    } catch (e) { toast.error(formatApiError(e, "Failed")); }
    finally { setSeeding(false); }
  };

  return (
    <div className="p-6" data-testid="crm-settings">
      <h1 className="font-display font-black text-3xl text-[#0B0D17] mb-1">CRM Settings</h1>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <p className="text-sm text-[#4A4D57]">Configure lookups used across the CRM.</p>
        <button onClick={seedNow} disabled={seeding} className="text-xs vp-btn-ghost inline-flex items-center gap-1" data-testid="cfg-seed-defaults">
          {seeding ? "Seeding…" : "Seed defaults"}
        </button>
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <div className="rounded-xl border border-[#E4E5E9] bg-white p-2">
            {TYPES.map((t) => (
              <button key={t.k} onClick={() => setActive(t.k)} className={"w-full text-left px-3 py-2 rounded-lg text-sm " + (active === t.k ? "bg-[#0B5CFF]/10 text-[#0B5CFF] font-semibold" : "hover:bg-[#F5F5F7]")} data-testid={`settings-type-${t.k}`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <div className="rounded-xl border border-[#E4E5E9] bg-white p-4">
            <div className="flex gap-2 mb-4">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a value…" className="flex-1 h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid="cfg-new-name" />
              <button onClick={add} className="vp-btn-primary text-sm inline-flex items-center gap-1" data-testid="cfg-add"><Plus className="w-4 h-4" /> Add</button>
            </div>
            {items.length === 0 && <div className="text-sm text-[#747680] py-4">No items yet.</div>}
            <ul className="divide-y divide-[#E4E5E9]">
              {items.map((it) => (
                <li key={it.id} className="py-2.5 flex items-center justify-between">
                  <span className="text-sm text-[#0B0D17]">{it.name}</span>
                  <button onClick={() => remove(it.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" data-testid={`cfg-del-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
